import { useAppSelector } from "@/redux/hooks";
import { ThemeDraft } from "@/utils/ThemeDraft";
import {
  buildThemeFromServerTheme,
  parseLegacyDtcThemeResponse,
  useUpdateCustomTheme,
  isThemeDirty,
} from "@brandclub/common-ui";
import { ThemeOptions } from "@mui/material";
import { produce } from "immer";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Outlet } from "react-router-dom";
const LandingPageThemeSetup = lazy(() => import("./LandingPageThemeSetup"));

interface ThemeSetupData {
  colorOptions: string[];
  fontOptions: string[];
  theme: ThemeOptions;
  draftId: string | undefined;
  setupId: string;
}

const ThemeSetupOutlet = () => {
  const [themeFromSetup, setThemeFromSetup] =
    useState<ThemeSetupData | undefined>();
  const [themeOptions, setThemeOptions] = useUpdateCustomTheme();
  const appId = useAppSelector(
    (state) => state.appConfig?.brandAppConfig?.appId
  );
  const setupId = themeFromSetup?.setupId;
  const initialThemeOptions = useRef<ThemeOptions | null>(null);
  const hasUnsavedThemeChanges = useMemo(
    () =>
      isThemeDirty({
        initialTheme: initialThemeOptions.current,
        currentTheme: themeOptions,
        themeFromSetup: themeFromSetup?.theme,
      }),
    [themeOptions, themeFromSetup?.theme]
  );

  const updateTheme: typeof setThemeOptions = useCallback(
    (args) => {
      if (Object.keys(initialThemeOptions.current ?? {}).length === 0) {
        // store the initial theme options to be able to reset to it
        initialThemeOptions.current = themeOptions;
      }
      return setThemeOptions(args, true);
    },
    [setThemeOptions, themeOptions]
  );

  const resetTheme = useCallback(
    (newInitialTheme?: ThemeOptions) => {
      if (newInitialTheme) {
        // reset what we consider to be the initial theme
        initialThemeOptions.current = newInitialTheme;
        setThemeFromSetup((prev) =>
          produce(prev, (draft) => {
            if (draft) {
              // keeps initialThemeOptions ref in sync with our local themeFromSetup to avoid false positive on isDirtyTheme checks
              (draft.theme as ThemeOptions) = newInitialTheme;
            }
          })
        );
      }
      if (initialThemeOptions.current) {
        updateTheme(initialThemeOptions.current);
      }
    },
    [updateTheme]
  );

  useEffect(() => {
    if (!appId || !!themeFromSetup) {
      return;
    }
    (async () => {
      const themeSetup = await ThemeDraft.getSetupTheme(appId);
      if (!themeSetup) {
        setThemeFromSetup(undefined);
        return;
      }
      const parsedTheme = parseLegacyDtcThemeResponse(themeSetup.theme, true);
      const theme = buildThemeFromServerTheme(parsedTheme, true);
      setThemeFromSetup({ ...themeSetup, theme: theme ?? {} });
      if (theme) {
        updateTheme(theme);
      }
    })();
  }, [appId, updateTheme, themeFromSetup]);

  useEffect(() => {
    // notify the user of unsaved theme changes when navigating away from the page
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      try {
        if (hasUnsavedThemeChanges) {
          // notify user that their changes are unsaved. if there is no serverTheme, there are no changes to worry about
          event.preventDefault();
        }
      } catch (_) {
        // silently fail
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedThemeChanges]);

  return (
    <>
      <Suspense fallback={null}>
        {!!setupId && (
          <LandingPageThemeSetup
            themeOptions={themeOptions}
            themeFromSetup={themeFromSetup}
            updateTheme={updateTheme}
            resetTheme={resetTheme}
            disabled={!hasUnsavedThemeChanges}
          />
        )}
      </Suspense>
      <Outlet />
    </>
  );
};

export default ThemeSetupOutlet;
